import {
    useContext,
    useEffect,
    useState
} from 'react';
import {
    Col,
    Flex,
    Input,
    Modal,
    Row,
    Segmented
} from 'antd';
import {
    Error,
    Fieldset,
    Legend
} from '../styled-components/form';
import {
    Controller,
    SubmitHandler,
    useForm
} from 'react-hook-form';
import {
    SegmentEnum
} from '../constants';
import {
    useLazyQuery,
    useMutation
} from '@apollo/client';
import {
    NotificationContext
} from '../context/notification-provider';
import { useSelector } from "react-redux";
import { selectRestaurant } from '../store/selectors/user.selector';
import { RETRIEVE_TIME_SLOT_BY_ID } from '../apollo/query/time-slot.query';
import { INSERT_TIME_SLOT, UPDATE_TIME_SLOT } from '../apollo/mutation/time-slot.mutation';

export interface PropTypes {
    id: number | null;
    visible: number | null;
    setVisible: (state: null | number) => void;

}
const TimeSlotDetail = (props: PropTypes) => {
    /** Retrieve component properties */
    const { id, visible, setVisible } = props;
    /** Retrieve the restaurant ID */
    const restaurant = useSelector(selectRestaurant);
    /** State to manage the tab selected */
    const [tab, setTab] = useState<string>(SegmentEnum.GeneralInformation);
    /** Notification utilities */
    const { showNotification } = useContext(NotificationContext);
    /** Formulary configuration */
    const { control, handleSubmit, formState, reset } = useForm({
        mode: 'onChange',
        defaultValues: {
            nIDRestaurant: restaurant?.nID,
            tStart: '',
            tEnd: '',
            nLimit: 0
        }
    });
    /** Manage to retrieve a category by id */
    const [ retrieveTimeSlotById, { data } ] = useLazyQuery(RETRIEVE_TIME_SLOT_BY_ID);
    /** Manage to insert a category */
    const [ insertTimeSlot, { data: responseInsertion } ] = useMutation(INSERT_TIME_SLOT, { refetchQueries: ['gqlRetrieveTimeSlots'] });
    /** Manage to update a category */
    const [ updateTimeSlot, { data: responseUpdation } ] = useMutation(UPDATE_TIME_SLOT, { refetchQueries: ['gqlRetrieveTimeSlots'] });
    
    /** Manage to retrieve the information of the category if we have an id */
    useEffect((): void => {
        reset({
            nIDRestaurant: restaurant?.nID,
            tStart: '',
            tEnd: '',
            nLimit: 0
        });
        if ( id && id !== -1) {
            retrieveTimeSlotById({ fetchPolicy: 'no-cache', variables: { nID: id } })
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);
    
    /** Manage to initialize the formulary with the category retrieved */
    useEffect((): void => {
        if( data?.data ) {
            const { nIDRestaurant, tStart, tEnd, nLimit } = data?.data;

            reset({ nIDRestaurant, tStart, tEnd, nLimit});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    /** Manage to close the category modal */
    const onClose = (): void => {
        setTab(SegmentEnum.GeneralInformation);
        reset();
        setVisible(null);
    }

    /** Manage to show the response alert when updation or insertion is done */
    useEffect((): void => {
        // eslint-disable-next-line eqeqeq
        if( responseInsertion?.code == 200 || responseUpdation?.code == 200 ) {
            showNotification({ severity: 'success', title: id && id !== -1 ? 'Franja Horaria actualitzada!' : 'Nova franja horaria afegida' });
            onClose();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [responseInsertion, responseUpdation]);
    
    /** Manage to request the insertion and updation of a category */
    const onSubmit: SubmitHandler<any> = (data): void => {
        if( id && id !== -1) {
            updateTimeSlot({ variables: { nID: id, timeSlot: { ...data, nLimit: parseInt(data.nLimit) }} });
        } else {
            insertTimeSlot({ variables: { timeSlot: { ...data, nLimit: parseInt(data.nLimit) }} });
        }
    }
    
    return (
        <Modal
            title="Franja Horaria"
            open={!!visible}
            onOk={handleSubmit(onSubmit)}
            okButtonProps={{ disabled: !formState.isValid }}
            onCancel={() => onClose()}
        >
            <Flex vertical>
                <Segmented
                    value={tab}
                    style={{ marginBottom: 8 }}
                    onChange={setTab}
                    options={[SegmentEnum.GeneralInformation]}
                />

                <form onSubmit={handleSubmit(onSubmit)}>
                    { tab === SegmentEnum.GeneralInformation && 
                        <Flex vertical align='flex-end'>
                            <Row style={{ width: '100%' }}>
                                <Col span={22}>
                                    <Controller
                                        name="tStart"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field, fieldState }) => (
                                            <Fieldset $error={fieldState.invalid}>
                                                <Legend $error={fieldState.invalid}> Hora Inici </Legend>
                                                <input type='time' style={{ height: '100%', width: '100%', border: 'none' }} {...field} />
                                                { fieldState.invalid && <Error> El camp és obligatori </Error> }
                                            </Fieldset>
                                        )}
                                    />
                                </Col>
                            </Row>
                            <Row style={{ width: '100%' }}>
                                <Col span={22}>
                                    <Controller
                                        name="tEnd"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field, fieldState }) => (
                                            <Fieldset $error={fieldState.invalid}>
                                                <Legend $error={fieldState.invalid}> Hora Final </Legend>
                                                <input type='time' style={{ height: '100%', width: '100%', border: 'none' }} {...field} />
                                                { fieldState.invalid && <Error> El camp és obligatori </Error> }
                                            </Fieldset>
                                        )}
                                    />
                                </Col>
                            </Row>
                            <Row style={{ width: '100%' }}>
                                <Col span={22}>
                                    <Controller
                                        name="nLimit"
                                        control={control}
                                        rules={{ required: true }}
                                        render={({ field, fieldState }) => (
                                            <Fieldset $error={fieldState.invalid}>
                                                <Legend $error={fieldState.invalid}> Número màxim de reserves </Legend>
                                                <Input size="middle" style={{ height: '100%', width: '100%', border: 'none' }} {...field} />
                                                { fieldState.invalid && <Error> El camp és obligatori </Error> }
                                            </Fieldset>
                                        )}
                                    />
                                </Col>
                            </Row>
                        </Flex>
                    }
                </form>
            </Flex>
        </Modal>
    );
}

export default TimeSlotDetail;